import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  TrophyIcon,
  ChartBarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StudySession } from '../types';
import { t } from '../utils/translations';

interface SessionSummaryProps {
  session: StudySession;
  cardsStudied: number;
  correctAnswers: number;
  totalDuration: number;
  onContinue: () => void;
  isQuickStudy?: boolean;
  deckName?: string;
}

const SessionSummary: React.FC<SessionSummaryProps> = ({
  session,
  cardsStudied,
  correctAnswers,
  totalDuration,
  onContinue,
  isQuickStudy = false,
  deckName
}) => {
  const accuracy = cardsStudied > 0 ? Math.round((correctAnswers / cardsStudied) * 100) : 0;
  const incorrectAnswers = cardsStudied - correctAnswers;
  const averageTimePerCard = cardsStudied > 0 ? Math.round(totalDuration / cardsStudied) : 0;

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getPerformanceMessage = () => {
    if (accuracy >= 90) {
      return {
        message: "Excelente trabalho! 🎉",
        description: "Você dominou essas cartas!",
        color: "text-green-600 dark:text-green-400"
      };
    } else if (accuracy >= 75) {
      return {
        message: "Muito bem! 🌟",
        description: "Bom desempenho, continue assim!",
        color: "text-blue-600 dark:text-blue-400"
      };
    } else if (accuracy >= 60) {
      return {
        message: "Bom progresso! 📚",
        description: "Você está melhorando!",
        color: "text-yellow-600 dark:text-yellow-400"
      };
    } else {
      return {
        message: "Continue praticando! 💪",
        description: "A prática leva à perfeição!",
        color: "text-orange-600 dark:text-orange-400"
      };
    }
  };

  const performance = getPerformanceMessage();

  return (
    <div className="w-full max-w-2xl mx-auto px-4 sm:px-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 px-4 sm:px-6 py-6 sm:py-8 text-center">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrophyIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('study.sessionCompleted')}
          </h2>
          <p className="text-blue-100">
            {isQuickStudy ? 'Sessão de Estudo Rápido' : deckName || 'Sessão de Estudo'}
          </p>
        </div>

        {/* Performance Message */}
        <div className="px-4 sm:px-6 py-4 text-center border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-xl font-semibold ${performance.color} mb-1`}>
            {performance.message}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {performance.description}
          </p>
        </div>

        {/* Stats */}
        <div className="px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {/* Cards Studied */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {cardsStudied}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Cards Estudados
              </div>
            </div>

            {/* Accuracy */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {accuracy}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Precisão
              </div>
            </div>

            {/* Time Spent */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <ClockIcon className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatDuration(totalDuration)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Tempo Total
              </div>
            </div>

            {/* Average Time */}
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <StarIcon className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatDuration(averageTimePerCard)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Tempo/Card
              </div>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="flex justify-center space-x-8 mb-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {correctAnswers} Corretas
              </span>
            </div>
            <div className="flex items-center">
              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {incorrectAnswers} Incorretas
              </span>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={onContinue}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {isQuickStudy ? 'Voltar aos Decks' : 'Continuar'}
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionSummary;